/**
 * FilterViewModel — 查询区模型
 *
 * ## 架构角色
 * 提供查询区字段模型初始化、搜索、重置等生命周期与扩展能力。
 * 继承 ContainerModel（通过 ttx.models.ContainerModel.call(this, data)）。
 *
 * ## 事件（7 个，通过 on/execute 触发）
 * | 事件 | 说明 |
 * |------|------|
 * | beforeInit | 查询区字段模型初始化之前；无模型，可修改 data 元数据 |
 * | afterInit | 查询区字段模型初始化完成；可通过 this.get(itemName).getFromModel() 获取字段模型 |
 * | beforeFilterTreeUpdate | 查询区导航树渲染之前 |
 * | afterReportInit | 智能分析报表专用，同 afterInit |
 * | beforeSearch | 搜索前置；可修改传递到后端的参数 |
 * | afterResetClick | 重置按钮点击后；可修改参数 |
 * | afterSearch | 搜索完成 |
 *
 * ## 实现要点
 * - 查询条件拼接 or：condition.simpleVOs.push({ logicOp: 'or', conditions: simpleConditions })
 * - 隐藏整个查询区：this.setState('bHideFilterScheme', true)
 *
 * ## 使用示例
 * ```js
 * var fvm = new ttx.models.FilterViewModel({ items: [...] });
 * fvm.on('beforeSearch', function (params) { // 修改 params });
 * fvm.search();
 * fvm.getFilterModel('date').getFromModel().getValue();
 * ```
 */
ttx.models.register('FilterViewModel', function (modelType) {
  const filterViewModel = function (data) {
    // beforeInit：查询区字段模型初始化之前；无模型，可修改 data 元数据
    this.execute('beforeInit', data);
    // 继承 ContainerModel 构造
    ttx.models.ContainerModel.call(this, data);
    // 初始化查询区字段模型
    this._initFilterModels(data);
    // afterInit：查询区字段模型初始化完成
    this.execute('afterInit', data);
  };
  filterViewModel.prototype = ttx.utils.getPrototype(ttx.models.ContainerModel.prototype);
  filterViewModel.prototype.modelType = modelType;

  // 初始化查询区字段模型：为每个 item 创建 FilterModel
  filterViewModel.prototype._initFilterModels = function (data) {
    if (!data) return;
    const items = data.items || data.children || data.controls || [];
    if (!items.length) return;
    const self = this;
    items.forEach(function (item) {
      const key = item.cItemName || item.field;
      if (!key) return;
      const filterModelData = ttx.utils.extend(true, {}, item);
      const filterModel = new ttx.models.FilterModel(filterModelData);
      filterModel.setParent(self);
      filterModel.setName(key);
      // 存入缓存与 _data
      const fieldModels = self.getCache('$filter') || {};
      fieldModels[key] = filterModel;
      self.setCache('$filter', fieldModels);
      self._set_data(key, filterModel, true);
    });
  };

  // 获取查询区字段的 FilterModel
  filterViewModel.prototype.getFilterModel = function (fieldName) {
    const fieldModels = this.getCache('$filter');
    if (fieldModels && fieldModels[fieldName]) return fieldModels[fieldName];
    // 兜底从 _data 取
    return this._get_data(fieldName);
  };

  // 收集查询参数
  // 区间条件按 MDF 或格式拼接：condition.simpleVOs.push({ logicOp: 'or', conditions: simpleConditions })
  filterViewModel.prototype._collectParams = function () {
    const params = {};
    const condition = { simpleVOs: [] };
    const fieldModels = this.getCache('$filter') || {};
    Object.keys(fieldModels).forEach(function (key) {
      const fm = fieldModels[key];
      if (!fm) return;
      const fromModel = fm.getFromModel && fm.getFromModel();
      const toModel = fm.getToModel && fm.getToModel();
      const fromValue = fromModel && fromModel.getValue ? fromModel.getValue() : undefined;
      if (toModel) {
        // 区间条件，拼接 or 查询
        const toValue = toModel.getValue ? toModel.getValue() : undefined;
        params[key] = { from: fromValue, to: toValue };
      } else if (fromValue !== undefined && fromValue !== null && fromValue !== '') {
        params[key] = fromValue;
      }
    });
    params.condition = condition;
    return params;
  };

  // 触发搜索，触发 beforeSearch 事件
  filterViewModel.prototype.search = function () {
    const params = this._collectParams();
    // beforeSearch：可修改传递到后端的参数
    if (this.execute('beforeSearch', params) === false) return null;
    this.execute('afterSearch', params);
    return params;
  };

  // 触发重置，触发 afterResetClick 事件
  filterViewModel.prototype.reset = function () {
    // 清空各 FilterModel 的值
    const fieldModels = this.getCache('$filter') || {};
    Object.keys(fieldModels).forEach(function (key) {
      const fm = fieldModels[key];
      const fromModel = fm && fm.getFromModel && fm.getFromModel();
      if (fromModel && typeof fromModel.clear === 'function') fromModel.clear(false);
      const toModel = fm && fm.getToModel && fm.getToModel();
      if (toModel && typeof toModel.clear === 'function') toModel.clear(false);
    });
    const params = this._collectParams();
    // afterResetClick：重置按钮点击后；可修改参数
    this.execute('afterResetClick', params);
    return params;
  };

  return filterViewModel;
});
